import config from '../config';
import React, { useState, useEffect } from "react";
import "../style/deviceList.css";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Tablet } from "lucide-react";
import axios from "axios";
import { useAuth } from "../context/authContext";
import Button from "../components/Button";
import DeviceModal from "../components/DeviceModal";

const DeviceList = () => {
  const { authData } = useAuth();
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [sortOrder, setSortOrder] = useState("A-Z");
  const [modalSearch, setModalSearch] = useState("");
  const location = useLocation();
  const { orgId: OrganizationId, orgName } = location.state || {};
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("success"); // or "error"

  const [fetchedDevices, setFetchedDevices] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [unassociatedDevices, setUnassociatedDevices] = useState([]);
  const [loading, setLoading] = useState({
    devices: true,
    unassociatedDevices: true,
    saving: false,
  });
  const [error, setError] = useState({
    devices: null,
    unassociatedDevices: null,
    saving: null,
  });

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        setLoading((prev) => ({ ...prev, devices: true }));
        setError((prev) => ({ ...prev, devices: null }));

        const response = await axios.get(
          `${config.BASE_URL}/devices/organization/${OrganizationId}`,
          {
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        setFetchedDevices(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error("Error fetching associated devices:", error);
        setError((prev) => ({ ...prev, devices: error.message }));
      } finally {
        setLoading((prev) => ({ ...prev, devices: false }));
      }
    };

    if (OrganizationId) {
      fetchDevices();
    }
  }, [OrganizationId, authData.accessToken, location.state]);

  useEffect(() => {
    const fetchUnassociatedDevices = async () => {
      try {
        setLoading((prev) => ({ ...prev, unassociatedDevices: true }));
        setError((prev) => ({ ...prev, unassociatedDevices: null }));

        const response = await axios.get(
          `${config.BASE_URL}/devices`,
          {
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        setUnassociatedDevices(
          Array.isArray(response.data) ? response.data : []
        );
      } catch (error) {
        console.error("Error fetching unassociated devices:", error);
        setError((prev) => ({ ...prev, unassociatedDevices: error.message }));
      } finally {
        setLoading((prev) => ({ ...prev, unassociatedDevices: false }));
      }
    };

    fetchUnassociatedDevices();
  }, [authData.accessToken]);

  const unassociated = unassociatedDevices.filter(
    (device) => device.OrganizationId === null
  );

  const clearSearch = () => {
    setSearch("");
  };

  const handleDeviceClick = (device) => {
    navigate(`/organization/${organizationId}/devices/${device.id}`, {
      state: { device, orgId: organizationId },
    });
  };

  const handleBackClick = () => {
    navigate(`/organization/${organizationId}`);
  };

  const handleAssociateClick = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleDeviceSelection = (device) => {
    setSelectedDevice(device);
  };

  const handleSave = async () => {
    if (!selectedDevice) return;

    setLoading((prev) => ({ ...prev, saving: true }));
    setError((prev) => ({ ...prev, saving: null }));

    try {
      await axios.put(
        `${config.BASE_URL}/devices/${selectedDevice.id}`,
        {
          OrganizationId: OrganizationId,
          device: JSON.stringify({
            deviceName: selectedDevice.device.deviceName,
            model: selectedDevice.device.model,
          }),
        },
        {
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      setFetchedDevices((prev) => [...prev, selectedDevice]);
      setUnassociatedDevices((prev) =>
        prev.filter((device) => device.id !== selectedDevice.id)
      );
      setShowModal(false);
      setSelectedDevice(null);

      const isUpdate = fetchedDevices.some((d) => d.id === selectedDevice.id);

      setPopupMessage(
        isUpdate
          ? "Device updated successfully!"
          : "Device associated successfully!"
      );
      setPopupType("success");
      setShowPopup(true);

      setTimeout(() => {
        setShowPopup(false);
      }, 4000);
    } catch (error) {
      console.error("Error updating device association:", error);
      setError((prev) => ({ ...prev, saving: error.message }));

      setPopupType("error");
      setShowPopup(true);

      setTimeout(() => {
        setShowPopup(false);
      }, 4000);
    } finally {
      setLoading((prev) => ({ ...prev, saving: false }));
    }
  };

  const normalize = (str) => {
    if (!str) return '';
    return str
      .toLowerCase()
      .replace(/[\u2018\u2019\u201A\u201B\u2039\u203A]/g, "'") 
      .replace(/'s\b/g, 's') 
      .replace(/'/g, '')     
      .replace(/\s+/g, ' ')
      .trim();
  };
  
  const searchWords = normalize(search).split(' ').filter(Boolean); 
  
  const sortedDevices = Array.isArray(fetchedDevices)
    ? fetchedDevices
        .filter((device) => {
          const deviceName = normalize(device?.device?.deviceName);
          const deviceId = normalize(device?.device?.id?.toString());
  
          return searchWords.every(
            (word) => deviceName.includes(word) || deviceId.includes(word)
          );
        })
        .sort((a, b) => {
          const nameA = a?.device?.deviceName || '';
          const nameB = b?.device?.deviceName || '';
          return sortOrder === 'A-Z'
            ? nameA.localeCompare(nameB)
            : nameB.localeCompare(nameA);
        })
    : [];
  
  
 
  const isLoading = loading.devices || loading.unassociatedDevices;

  return (
    <div className="deviceList-page">
      {showPopup && <div className={`popup-${popupType}`}>{popupMessage}</div>}

      <div className="deviceList-content-wrapper">
        <div className="deviceList-header">
          <Button
            className="deviceList-backButton"
            onClick={handleBackClick}
            text="← Back"
            color="#006d7d"
          />
          <h1>Devices</h1>
        </div>

        {isLoading ? (
          <div className="deviceList-loadingContainer">
            <div className="deviceList-loader"></div>
            <p>Loading devices...</p>
          </div>
        ) : error.devices ? (
          <div className="deviceList-errorContainer">
            Error: {error.devices}
          </div>
        ) : (
          <div className="deviceList-container">
            <div className="deviceList-searchContainer">
              <input
                type="text"
                className="deviceList-searchInput"
                placeholder="Search for..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <Button
                className="deviceList-clearButton"
                onClick={clearSearch}
                text="Clear"
                color={search ? "#006d7d" : "grey"}
              />
              <div className="deviceList-sort">
                <span className="deviceList-sortLabel">Sort By:</span>
                <select
                  className="deviceList-sortSelect"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                >
                  <option value="A-Z">A → Z</option>
                  <option value="Z-A">Z → A</option>
                </select>
              </div>
              <Button
                className="deviceList-associateButton"
                onClick={handleAssociateClick}
                text="Associate Device"
                color="#006d7d"
                disabled={loading.unassociatedDevices}
              />
            </div>

            <div className="deviceList-list">
              {sortedDevices.length > 0 ? (
                sortedDevices.map((device, index) => (
                  <div
                    className="deviceList-item"
                    key={device.id || index}
                    onClick={() => handleDeviceClick(device)}
                  >
                    <Tablet />
                    <div className="deviceList-info">
                      <strong className="deviceList-name">
                        {device.device.deviceName || ""}
                      </strong>
                      <small className="deviceList-model">
                        {device.device.model || ""}
                      </small>
                    </div>
                  </div>
                ))
              ) : (
                <div className="deviceList-noDevices">No devices found.</div>
              )}
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <DeviceModal
          showModal={showModal}
          handleCloseModal={handleCloseModal}
          modalSearch={modalSearch}
          setModalSearch={setModalSearch}
          selectedDevice={selectedDevice}
          handleDeviceSelection={handleDeviceSelection}
          handleSave={handleSave}
          loading={loading.saving}
          error={error.saving}
          organizationId={organizationId}
          unassociatedDevices={unassociated}
          loadingDevices={loading.unassociatedDevices}
          errorDevices={error.unassociatedDevices}
        />
      )}
    </div>
  );
};

export default DeviceList;
